import React from 'react';
import { motion } from 'motion/react';
import { Check, Shield, Zap, Globe, Clock, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import SEO from '@/src/components/SEO';

const PACKAGES = [
  {
    name: "Cybersecurity Risk Audit",
    subtitle: "Entry-Level Assessment",
    price: "299",
    timeline: "2-3 Days",
    description: "Identify critical vulnerabilities before they are exploited. Perfect for small businesses and startups.",
    features: [
      "Vulnerability Review",
      "Quantified Risk Score",
      "Key Findings Report",
      "Prioritized Action Plan",
      "Cloud Config Review"
    ],
    cta: "Request Risk Audit",
    popular: false
  },
  {
    name: "Continuous Monitoring",
    subtitle: "Automated Protection & Review",
    price: "300",
    timeline: "Ongoing",
    description: "Continuous automated threat detection with monthly expert reviews of your security posture.",
    features: [
      "Automated Threat Detection",
      "Real-time Security Alerts",
      "Monthly Expert Review",
      "Vulnerability Management",
      "Endpoint Security Support",
      "Incident Response Guidance"
    ],
    cta: "Request Setup",
    popular: true
  },
  {
    name: "Security Implementation",
    subtitle: "Strategic Hardening",
    price: "Custom",
    timeline: "Project Based",
    description: "Full-scale security architecture and implementation tailored to your specific needs.",
    features: [
      "Custom Security Architecture",
      "Compliance Implementation",
      "Advanced IAM Setup",
      "Zero Trust Framework",
      "DevSecOps Integration",
      "Dedicated Security Lead"
    ],
    cta: "Request Custom Quote",
    popular: false
  }
];

export default function Pricing() {
  return (
    <div className="relative pt-24 pb-24 overflow-hidden">
      <SEO 
        title="Pricing & Packages"
        description="Transparent pricing for cybersecurity risk audits, continuous monitoring, and strategic security implementation by AGRANDINTL."
      />
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,150,255,0.02),transparent_50%)]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="text-center mb-16 space-y-3">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em]"
          >
            <Shield className="w-3 h-3" /> Transparent Consulting
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight"
          >
            Investment in <br />
            <span className="text-accent">Resilience.</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-sm md:text-base text-muted-foreground font-normal max-w-xl mx-auto leading-relaxed"
          >
            Choose the level of defense that matches your risk profile. No hidden fees, just professional security solutions.
          </motion.p>
        </div>

        {/* Pricing Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {PACKAGES.map((pkg, i) => (
            <motion.div
              key={pkg.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`relative glass-card rounded-2xl p-6 flex flex-col border-white/5 ${pkg.popular ? 'border-accent/20 bg-accent/[0.01]' : ''}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-accent text-background text-[8px] font-bold uppercase tracking-widest rounded-full">
                  Most Recommended
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-lg font-display font-bold mb-0.5">{pkg.name}</h3>
                <p className="text-[8px] text-muted-foreground font-bold uppercase tracking-widest">{pkg.subtitle}</p>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-[8px] font-bold text-muted-foreground uppercase tracking-wider">
                    {pkg.price === 'Custom' ? 'Contact for' : 'Starting from'}
                  </span>
                  <span className="text-xl font-display font-bold">
                    {pkg.price === 'Custom' ? 'Custom Pricing' : `$${pkg.price}`}
                  </span>
                  {pkg.name === "Monthly Monitoring" && (
                    <span className="text-[10px] text-muted-foreground font-medium">/ month</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 text-[8px] text-muted-foreground">
                  <Clock className="w-2.5 h-2.5" />
                  <span>Timeline: {pkg.timeline}</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mb-4 leading-relaxed italic">
                {pkg.description}
              </p>

              <div className="space-y-2 mb-6 flex-grow">
                {pkg.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-2.5">
                    <Check className="w-3 h-3 text-accent shrink-0 mt-0.5" />
                    <span className="text-xs font-medium text-foreground/80">{feature}</span>
                  </div>
                ))}
              </div>

              <Button 
                asChild
                variant={pkg.popular ? 'default' : 'outline'}
                className={`w-full h-10 rounded-xl font-bold group text-xs ${pkg.popular ? 'bg-accent text-background hover:bg-accent/90' : 'border-accent/10 hover:bg-accent/5'}`}
              >
                <Link to="/contact">
                  {pkg.cta} <ArrowRight className="ml-2 w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Assurance Section */}
        <div className="max-w-2xl mx-auto glass-card rounded-2xl p-8 border-accent/10 bg-accent/[0.01] text-center space-y-4">
          <div className="w-9 h-9 rounded-xl bg-accent/5 flex items-center justify-center text-accent mx-auto">
            <Globe className="w-4.5 h-4.5" />
          </div>
          <h2 className="text-xl font-display font-bold">Need a Custom Quote?</h2>
          <p className="text-sm text-muted-foreground font-normal leading-relaxed">
            Every business is unique. If our standard packages don't fit your specific architecture or compliance needs, we can design a bespoke security roadmap for you.
          </p>
          <Button asChild size="lg" className="h-11 px-6 rounded-xl font-bold text-xs">
            <Link to="/contact">Request Custom Quote</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
